import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import IconNotification from '../icons/IconNotification';
import IconSettingHeader from '../icons/IconSettingHeader';
import BookMark from './components/BookMark';
import EmulationWatermark from './components/EmulationWatermark';
import ProfileDetails from './ProfileDetails';
import ReportBug from './components/ReportBug';
import SearchBlock from './components/SearchBlock';
import { readEmulationState } from '../../utils/emulation';
import { STORAGE_KEYS } from '../../constants/storageKeys';
import IconClose from '../icons/IconClose';
import IconSearch from '../icons/IconSearch';

const APP_LOGO_SRC = '/RWLogoNew.svg';
const APP_LOGO_HEIGHT = 30;

// ── Header shell ────────────────────────────────────────────────────────────
// At ≥769px: horizontal flex row with a 16-px gap between menu / search / profile.
// At ≤768px: vertical stack — the search collapses, the mobile actions show.
//
// NOTE: no z-index boost here. The header used to carry
// `max-[768px]:relative z-[6000]` to sit above the retired hamburger
// aside-drawer's backdrop; with that drawer gone the boost only caused
// the header (logo cluster + search) to float ABOVE the mobile filter
// slide-overs (scrim z-1200 / drawer z-1201), overlapping their top
// edge. Overlays are expected to cover the header.
const HEADER_CLASS =
  'flex items-center justify-between gap-4 py-[10px] px-6 min-h-[56px]' +
  ' bg-[var(--color-header-bg)] text-[var(--color-header-fg)]' +
  ' border-b border-solid border-[var(--color-border)]' +
  // Mobile: brand dark-green header strip (the rail's #031c03) with white
  // foreground — EXCEPT the logo cluster, which sits in a full-bleed cream
  // (#e5e1d8) block (mirrors the rail: cream logo box on a dark-green rail).
  // No green bottom border — it read as an unwanted green divider line.
  ' max-[768px]:bg-[#031c03] max-[768px]:text-white max-[768px]:border-b-0' +
  // Asymmetric mobile padding: left stays 1.5rem (cancelled by the cream
  // logo block's -ml-6 so it sits flush), right is a tight 0.5rem so the
  // bell + avatar hug the edge.
  ' max-[768px]:flex-col max-[768px]:items-stretch max-[768px]:gap-2 max-[768px]:py-2 max-[768px]:pl-6 max-[768px]:pr-2';

const HEADER_MENU_BLOCK_CLASS =
  'flex items-center gap-4 flex-1 min-w-0' +
  ' max-[768px]:flex-col max-[768px]:items-stretch max-[768px]:gap-2';

const HEADER_MENU_CLASS =
  'flex items-center gap-3 max-[768px]:justify-between';

// Left cluster of the mobile header row: the RW logo inside a CREAM/sand
// (#e5e1d8 — the rail's logo-box colour) block, against the brand-green
// header. Per the mobile design the block is FULL-BLEED: it stretches to
// the header's top/bottom/left edges (negative margins cancel the
// header's mobile `py-2 px-6` padding) with square corners — a mirror of
// the desktop rail's flush cream logo box. MOBILE-ONLY — hidden at
// >=769px because the aside rail already shows the RW TEAM logo there.
// On mobile the rail is hidden and navigation lives in the BottomTabBar,
// so the header only carries the brand mark (no hamburger).
const HEADER_MENU_LEFT_CLASS =
  'hidden items-center justify-center flex-none' +
  ' max-[768px]:flex max-[768px]:self-stretch max-[768px]:bg-[#e5e1d8]' +
  ' max-[768px]:-my-2 max-[768px]:-ml-6 max-[768px]:px-4';
// Larger brand mark on mobile so it fills the full-bleed cream block.
const HEADER_LOGO_CLASS = 'flex-none max-[768px]:h-[40px] max-[768px]:w-auto';

const HEADER_SEARCH_WIDE_CLASS =
  'flex-1 flex justify-start max-w-[500px] max-[768px]:hidden';

// Mobile search wrapper — `flex-1 min-w-0` lets the search shrink so the
// bell + profile actions always fit on the row (the inner SearchBlock's
// `min-w-[320px]` is overridden to 0 on the mini instance below).
const HEADER_SEARCH_MINI_CLASS =
  'hidden max-[768px]:block max-[768px]:flex-1 max-[768px]:min-w-0';

// Mobile search is COLLAPSED to an icon by default (the always-visible
// input crowded the row — see QA 2026-08-20); tapping the icon mounts
// the expanded field. While expanded, the bell + avatar hide so the
// input gets the full row, and a labelled ✕ collapses it back.

// Override the SearchBlock's 320px min-width on the mobile (mini) instance
// so it can shrink and leave room for the bell + avatar.
const HEADER_SEARCH_MINI_INNER_CLASS = '!min-w-0';

// `flex-none` keeps the bell + profile from being squeezed out / clipped
// when the search fills the row.
const HEADER_MOBILE_ACTIONS_CLASS =
  'hidden max-[768px]:inline-flex max-[768px]:items-center max-[768px]:gap-[6px] max-[768px]:flex-none';

const HEADER_PROFILE_BLOCK_CLASS =
  'flex items-center gap-[6px] flex-none max-[768px]:hidden';

// 32×32 round icon button (notifications, settings, etc.)
const HEADER_ICON_BTN_CLASS =
  'inline-flex items-center justify-center w-8 h-8 border-0 rounded-full bg-transparent' +
  ' text-[var(--color-text-secondary)] transition-colors duration-fast' +
  ' enabled:hover:bg-[var(--color-bg-hover)] enabled:hover:text-[var(--color-text-primary)]' +
  ' disabled:opacity-50';
const HEADER_ICON_BTN_CURSOR =
  'cursor-pointer disabled:cursor-not-allowed';

// Mobile (≤768px) icon buttons sit on the dark-green brand strip, so
// they paint with the on-primary text token. Two variants, no `!`:
// the plain media variant out-cascades the base text colour, and the
// media-wrapped enabled:hover variant matches the hover rule's
// specificity while being emitted later — so hover can't flip the
// icon dark on the green surface.
const HEADER_MOBILE_ICON_CLASS =
  `${HEADER_ICON_BTN_CLASS} ${HEADER_ICON_BTN_CURSOR}` +
  ' max-[768px]:text-[var(--color-text-on-primary)]' +
  ' max-[768px]:enabled:hover:text-[var(--color-text-on-primary)]';

const HeaderIndex: React.FC = () => {
  const { t } = useTranslation();
  const [emulating, setEmulating] = useState<boolean>(
    () => readEmulationState().active,
  );
  /** Mobile-only: whether the header search is expanded to a field
   *  (default: collapsed to an icon). */
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== STORAGE_KEYS.isEmulate) return;
      setEmulating(readEmulationState().active);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <>
      {emulating ? <EmulationWatermark /> : null}
      <div id="header-index-id" className={HEADER_CLASS}>
        <div className={HEADER_MENU_BLOCK_CLASS}>
          <div className={HEADER_MENU_CLASS}>
            <span className={HEADER_MENU_LEFT_CLASS}>
              <img
                src={APP_LOGO_SRC}
                alt={t('header.appName')}
                height={APP_LOGO_HEIGHT}
                className={HEADER_LOGO_CLASS}
              />
            </span>

            {mobileSearchOpen ? (
              <div className={HEADER_SEARCH_MINI_CLASS}>
                <SearchBlock
                  className={HEADER_SEARCH_MINI_INNER_CLASS}
                  autoFocus
                />
              </div>
            ) : null}

            <div className={HEADER_MOBILE_ACTIONS_CLASS}>
              {mobileSearchOpen ? (
                <button
                  type="button"
                  className={HEADER_MOBILE_ICON_CLASS}
                  onClick={() => setMobileSearchOpen(false)}
                  aria-label={t('header.closeSearch', 'Close search')}
                  title={t('header.closeSearch', 'Close search')}
                >
                  <IconClose size={18} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className={HEADER_MOBILE_ICON_CLASS}
                    onClick={() => setMobileSearchOpen(true)}
                    aria-label={t('header.openSearch', 'Open search')}
                    title={t('header.openSearch', 'Open search')}
                  >
                    <IconSearch size={19} ariaHidden />
                  </button>
                  <button
                    type="button"
                    className={HEADER_MOBILE_ICON_CLASS}
                    disabled
                    aria-label={t('header.notifications')}
                    title={t('header.notifications')}
                  >
                    <IconNotification size={20} />
                  </button>
                  <ProfileDetails showName={false} />
                </>
              )}
            </div>
          </div>

          <div className={HEADER_SEARCH_WIDE_CLASS}>
            <SearchBlock />
          </div>
        </div>

        <div className={HEADER_PROFILE_BLOCK_CLASS}>
          <ReportBug />
          <BookMark />
          <button
            type="button"
            className={`${HEADER_ICON_BTN_CLASS} ${HEADER_ICON_BTN_CURSOR}`}
            disabled
            aria-label={t('header.notifications')}
            title={t('header.notifications')}
          >
            <IconNotification size={20} />
          </button>
          <button
            type="button"
            className={`${HEADER_ICON_BTN_CLASS} ${HEADER_ICON_BTN_CURSOR}`}
            disabled
            aria-label={t('header.settings')}
            title={t('header.settings')}
          >
            <IconSettingHeader size={20} />
          </button>
          <ProfileDetails />
        </div>
      </div>
    </>
  );
};

export default HeaderIndex;
